import {
  AcillaryResponseInt,
  AncillariesResponseInt,
  BaggageAndServicesInt,
  BenefitBySegmentRequestInt,
  BenefitBySegmentResponseInt,
  CheckedBagsBenefitsInt,
  EmitTicketAncillaryRequestInt,
  PaymentCurrencyInt,
  PaymentFlightDetailInt,
  PaymentPassengerInt,
  PaymentPccInt,
  PaymentProductInt,
  PaymentTaxDetailInt,
  ResponseSeatInt,
} from '@app/interfaces/micro/ancillaries.interface';
import { Reservation } from '@app/models/micro/reservation.model';
import moment from 'moment';
import {
  CHECKIN_STATUS,
  PAX_TYPE,
  StatusBag,
  SeatPassangerEstatus,
  TypeBagsWeight,
} from '@app/enums';
import { FlightSummary, Passenger } from '@app/models/micro';
import { BagsPassangerInt } from '@app/interfaces/micro/bags.interface';
import { BagPassangerModel } from '@app/models/micro/bags.model';
import { PaymentInfoInt } from '@app/interfaces';
import { extractFfTier, resolveFfTier } from '@app/utils/functions/ff-tier';

const DEFAULT_CONSTANTS = {
  CURRENCY_CODE: 'MXN',
  COBRAND_CARD: 'AMEXPLT',
  REQUEST_FROM: 'Kiosk Self-Service',
  BOOKING_CABIN: 'Y',
  REMAINING_TIME: 1,
  ROLLBACK: false,
  TAX_CODE: 'MXA',
} as const;

const REDEMPTION_TYPES = {
  tier: 'TIER',
  cobrand: 'COBRAND',
  corporate: 'CORPORATE',
} as const;

const BAG_WEIGHT_MAPPING = {
  10: TypeBagsWeight._10KG,
  15: TypeBagsWeight._15KG,
  25: TypeBagsWeight._25KG,
  32: TypeBagsWeight._32KG,
} as const;

const NAMES_BAGS = {
  [TypeBagsWeight._10KG]: 'ST BAG UP TO 55LB 10KG',
  [TypeBagsWeight._15KG]: 'ST BAG UP TO 55LB 15KG',
  [TypeBagsWeight._25KG]: 'ST BAG UP TO 55LB 25KG',
  [TypeBagsWeight._32KG]: 'ST BAG UP TO 55LB 32KG',
} as const;

export class AncillariesMapper {
  //#region Funciones privadas

  //Mapea un segmento de vuelo a la estructura requerida por la API
  private static mapSegmentToApiFormat(segment: any, reservation?: Reservation) {
    return {
      segmentCode: segment.segmentCode,
      remainingSegmentTimeToCheckin: segment.remainingSegmentTimeToCheckin,
      aircraftType: segment.aircraftType,
      origin: segment.departureAirport,
      destination: segment.arrivalAirport,
      marketingCarrier: segment.marketingCarrier,
      operatingCarrier: segment.operatingCarrier,
      operatingFlightCode: segment.operatingFlightCode,
      marketingFlightCode: segment.marketingFlightCode,
      departureDate: segment.departureDateTime,
      arrivalDate: segment.arrivalDateTime,
      farebasis: segment.farebasis,
      fareFamily: segment.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment.fareFamily,
      bookingClass: segment.bookingClass,
      bookingCabin: segment.cabinClass,
      segmentNumber: segment.segmentNumber,
      segmentEntity: segment.segmentEntity,
      departureAirportTimeZoneId: segment.departureAirportTimeZoneId,
      ...(reservation && { segmentRegion: reservation.flightSummary.legRegion }),
    };
  }

  //Construye la estructura de currency estándar
  private static buildCurrencyStructure(currency?: any) {
    return {
      currencyCode: currency?.currencyCode || DEFAULT_CONSTANTS.CURRENCY_CODE,
      base: currency?.base || 0,
      taxDetails: Array.isArray(currency?.taxDetails)
        ? currency.taxDetails.map((tax) => ({
            ...tax,
            exempt: false,
          }))
        : currency?.taxDetails || [],
      totalTax: currency?.totalTax || 0,
      subTotal: currency?.subTotal || 0,
      total: currency?.total || 0,
      totalCPPoints: currency?.totalCPPoints || 0,
    };
  }

  //Mapea información básica de pasajero
  private static mapPassengerBasicInfo(passenger: Passenger) {
    return {
      nameNumber: passenger.nameRefNumber,
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      type: passenger.paxType,
      dateOfBirth: passenger.dateOfBirth,
    };
  }

  //Obtiene el tipo de peso de equipaje basado en el peso en kg
  private static getBagWeightType(weightKg: number): TypeBagsWeight {
    return BAG_WEIGHT_MAPPING[weightKg] || TypeBagsWeight._25KG;
  }

  //Crea maletas de equipaje de mano
  private static createCarryOnBags(carryOn: any): BagsPassangerInt[] {
    const bags: BagsPassangerInt[] = [];
    const typeCarryon = this.getBagWeightType(carryOn.weightKg);

    for (let i = 0; i < carryOn.quantity; i++) {
      const ancillaryCarryOn = BagPassangerModel.builder()
        .withTypeBagWeight(typeCarryon)
        .withCurrency(undefined)
        .withOrder(1)
        .build();
      bags.push(ancillaryCarryOn);
    }
    return bags;
  }

  //Crea maletas incluidas
  private static createCheckedBags(
    checkedBags: CheckedBagsBenefitsInt,
    hasInfant: boolean,
    infantCheckedBaggage: any
  ): BagsPassangerInt[] {
    const bags: BagsPassangerInt[] = [];
    const { quantities, quantity: qty } = checkedBags;
    let order = 1;
    const type = this.getBagWeightType(checkedBags.weightKg);
    const maxAllowed = checkedBags.maxBagsAllowed || 7;
    const quantity = qty > 0 ? qty : Math.min(quantities.ADT, maxAllowed);
    for (let i = 0; i < quantity; i++) {
      const commercialName = `${order}${NAMES_BAGS[type]}`;
      const ancillaryCheckedBag = BagPassangerModel.builder()
        .withTypeBagWeight(type)
        .withCommercialName(commercialName)
        .withCurrency(undefined)
        .withOrder(order)
        .build();

      bags.push(ancillaryCheckedBag);
      order++;
    }

    if (hasInfant && infantCheckedBaggage.quantity > 0) {
      const commercialName = `${order}${NAMES_BAGS[TypeBagsWeight._25KG]}`;
      const ancillaryCheckedBagInfant = BagPassangerModel.builder()
        .withTypeBagWeight(TypeBagsWeight._25KG)
        .withCommercialName(commercialName)
        .withCurrency(undefined)
        .withOrder(order)
        .build();
      bags.push(ancillaryCheckedBagInfant);
    }

    return bags;
  }

  /**
   * Ordena las maletas extra por orden comercial
   * @param extraBags Array de maletas a ordenar
   * @returns Array ordenado de maletas con propiedad order
   */
  private static orderExtraBag(extraBags: AcillaryResponseInt[]) {
    return extraBags
      .map((ancillary) => ({
        order: Number(ancillary.commercialName.substring(0, 1)) || 0,
        ...ancillary,
      }))
      .sort((a, b) => a.order - b.order);
  }

  //Mapea información de viajero para ancillaries request
  private static mapTravelerInfo(passengers: Passenger[]) {
    return passengers
      .filter((p) => p.isSelectedToCheckin || p.isSelectForBagagge)
      .map((passenger) => {
        const { nameRefNumber, lastName, firstName, dateOfBirth, passengerInfo, ticketInfo } =
          passenger;
        const { ffNumber, ffProgram, type } = passengerInfo;

        return {
          nameNumber: nameRefNumber,
          firstName,
          lastName,
          dateOfBirth,
          type,
          ticketNumber: ticketInfo?.eticket,
          loyaltyAccount: {
            frequentFlyerProgram: ffProgram || '',
            frequentFlyerNumber: ffNumber || '',
          },
        };
      });
  }

  //Mapea segmentos para ancillaries request
  private static mapSegmentsForAncillaries(segments: any[]) {
    return segments
      .filter((s) => s.windowSegmentStatus !== CHECKIN_STATUS.CLOSED)
      .map((segment) => {
        const flightNumber = segment.flightNumber.split(' ');
        const [, operatingFlightCode] = flightNumber;

        return {
          ...this.mapSegmentToApiFormat(segment),
          operatingFlightCode,
        };
      });
  }

  //Mapea información de asientos para un pasajero
  private static mapPassengerSeats(passenger: Passenger) {
    return passenger.seats.map((seat) => ({
      seatCode: seat?.seatCode,
      segmentCode: seat?.segmentCode,
      status: seat?.status,
      currency: seat?.currency,
    }));
  }

  //Mapea información de cupones para un pasajero
  private static mapPassengerCoupons(passenger: Passenger) {
    return (
      passenger?.ticketInfo?.coupons?.map((coupon) => ({
        couponNumber: coupon?.couponNumber,
        couponStatus: coupon?.couponStatus,
        segmentCode: coupon?.segmentCode,
      })) || []
    );
  }

  //Filtra segmentos que tienen asientos asignados
  // Incluye segmentos con asientos en seats, seatsNew, o el segmento del asiento que se está seleccionando
  private static filterSegmentsWithSeats(
    segments: any[],
    passengers: Passenger[],
    currentSeatSegmentCode?: string
  ) {
    return (
      segments?.filter((seg) => {
        // Verificar si algún pasajero tiene asiento en este segmento (seats o seatsNew)
        const hasSeatInSegment = passengers.some(
          (p) =>
            p.seats?.some((s) => s.segmentCode === seg.segmentCode) ||
            p.seatsNew?.some((s) => s.segmentCode === seg.segmentCode)
        );

        // Incluir también el segmento del asiento que se está seleccionando actualmente
        const isCurrentSeatSegment =
          currentSeatSegmentCode && seg.segmentCode === currentSeatSegmentCode;

        return hasSeatInSegment || isCurrentSeatSegment;
      }) || []
    );
  }
  //#endregion Funciones privadas

  //#region Funciones públicas

  /**
   * Genera request para obtener mapa de asientos
   * @param reservation Información de la reserva
   * @returns Objeto request para API de mapa de asientos
   */
  static seatMapRequest(reservation: Reservation) {
    return {
      reservationCode: reservation.pnr,
      isStandBy: reservation.isStandByBooking,
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      isNewBrandedFare: reservation.isNewBF,
      legs: [
        {
          legCode: reservation.flightSummary.legCode,
          legRegion: reservation.flightSummary.legRegion,
          windowLegStatus: 'CKI', //reservation.segments[0].windowSegmentStatus,
          segments: reservation.segments
            .filter((seg) => seg.windowSegmentStatus !== CHECKIN_STATUS.CLOSED)
            .map((segment) => ({
              ...this.mapSegmentToApiFormat(segment, reservation),
              currencyCode: DEFAULT_CONSTANTS.CURRENCY_CODE,
            })),
        },
      ],
    };
  }

  /**
   * Procesa respuesta del mapa de asientos
   * @param response Respuesta de la API
   * @returns Secciones del mapa de asientos
   */
  static seatMapResponse(response: any) {
    return response?.sections || [];
  }

  /**
   * Genera request para obtener servicios adicionales (ancillaries)
   * @param reservation Información de la reserva
   * @returns Objeto request para API de ancillaries
   */
  static getAncillariesRequest(reservation: Reservation) {
    const { passengers, pnr, flightSummary, creationDate: rawCreationDate } = reservation;
    const creationDate = rawCreationDate.split('T')[0];

    // CI-18515: Construir legCode del PRIMER SEGMENTO solamente
    // Para vuelos con conexión, el backend solo acepta el primer tramo
    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }

    // Fallback si no se puede construir
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    return {
      reservationCode: pnr,
      creationDate,
      travelerInfo: this.mapTravelerInfo(passengers),
      legs: [
        {
          legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
          legRegion: flightSummary?.legRegion,
          windowLegStatus: flightSummary?.windowLegStatus,
          // CI-18515: Enviar SOLO el primer segmento
          segments: firstSegment
            ? [this.mapSegmentToApiFormat(firstSegment, reservation)]
            : this.mapSegmentsForAncillaries(flightSummary?.segments || []),
        },
      ],
    };
  }

  /**
   * Procesa respuesta de servicios adicionales disponibles
   * @param response Respuesta de la API con carryOn, extraBags y overweight
   * @param legCode Código del tramo (actualmente no utilizado)
   * @returns Objeto con passengers y overweight procesados
   */
  static getAncillariesResponse(response: AncillariesResponseInt, legCode: string) {
    if (!response) {
      throw new Error('Response is required for getAncillariesResponse');
    }

    const { carryOn, extraBags, overweight } = response;
    let passengers: any[] = [];

    if (extraBags?.length > 0) {
      const bags = this.orderExtraBag(extraBags);
      passengers = extraBags[0].applyTo.map((referenceNumber) => ({
        referenceNumber, // Fixed typo from refererNumbrer
        extraBags: bags,
        carryOn: [],
      }));
    }

    if (carryOn?.length > 0) {
      const bags = this.orderExtraBag(carryOn);
      if (passengers.length === 0) {
        passengers = carryOn[0].applyTo.map((referenceNumber) => ({
          referenceNumber, // Fixed typo from refererNumbrer
          carryOn: bags,
          extraBags: [],
        }));
      } else {
        passengers.forEach((passenger) => {
          passenger.carryOn = bags;
        });
      }
    }

    return {
      overweight,
      passengers,
    };
  }

  static applyFfTierFromAncillaries(
    response: AncillariesResponseInt,
    reservation: Reservation
  ): void {
    for (const passenger of reservation?.passengers ?? []) {
      const fromAncillaries = extractFfTier(response, passenger.nameRefNumber);
      if (fromAncillaries) {
        passenger.ancillariesFfTier = fromAncillaries;
      }
    }
  }

  static resolvePassengerFfTier(passenger: Passenger): string {
    return resolveFfTier(
      passenger?.ancillariesFfTier,
      passenger?.frequentFlyerName || passenger?.passengerInfo?.ffName,
      passenger?.ffTierLevel || passenger?.passengerInfo?.ffTierLevel
    );
  }

  /**
   * Genera request para eliminar servicios adicionales
   * @param reservation Información de la reserva
   * @returns Objeto request para eliminar ancillaries
   */
  static removeAncillariesRequest(reservation: Reservation) {
    const { flightSummary, passengers } = reservation;

    // CI-18515: Construir legCode del PRIMER SEGMENTO solamente
    const firstSegment = flightSummary?.segments?.[0];
    let legCodeFirstSegment = '';
    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      legCodeFirstSegment = `${firstSegment.departureAirport}_${firstSegment.arrivalAirport}_${departureDate}`;
    }
    if (!legCodeFirstSegment) {
      legCodeFirstSegment = flightSummary?.legCode || '';
    }

    return {
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
      travelerInfo: passengers.map((passenger) => ({
        ...this.mapPassengerBasicInfo(passenger),
        ancillaries: [...passenger.bagStack, ...passenger.ancillaries, ...passenger.bagStackCarryon]
          .filter(
            (b) =>
              b.status === StatusBag.TO_PAY || b.status === StatusBag.ON_HOLD || b.isExtraWeight
          )
          // Solo incluir ancillaries que tienen un ID numérico del backend para evitar mandar ids generados localmente (bagStack, bagStackCarryon, bagOfferCarryOnStack)
          .filter((ancillary) => {
            const numericId = Number(ancillary.id);
            return !isNaN(numericId) && numericId > 0 && numericId < 1000;
          })
          .map((ancillary) => ({
            ancillary: {
              id: ancillary.id,
              rficSubcode: ancillary.rficSubcode || ancillary.code,
              groupCode: ancillary.groupCode,
              emdType: ancillary.emdType,
              rficCode: ancillary.rficCode,
              ssrCode: ancillary.ssrCode,
              owningCarrierCode: ancillary.owningCarrierCode,
              currency: this.buildCurrencyStructure(ancillary?.currency),
            },
            legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
            quantity: 0,
          })),
      })),
      legs: [
        {
          legCode: legCodeFirstSegment, // CI-18515: Usar solo primer segmento
          legRegion: flightSummary.legRegion,
          // CI-18515: Enviar SOLO el primer segmento
          segments: firstSegment
            ? [this.mapSegmentToApiFormat(firstSegment)]
            : flightSummary?.segments?.map((segment) => this.mapSegmentToApiFormat(segment)),
        },
      ],
    };
  }

  /**
   * Genera request para redimir servicios adicionales (tier, cobrand, corporate)
   * @param reservation Información de la reserva
   * @param passenger Información del pasajero
   * @param ancillary Servicio adicional a redimir
   * @param type Tipo de redención ('tier', 'cobrand', 'corporate')
   * @returns Objeto request para redimir ancillary
   */
  static redeemAncillariesRequest(
    reservation: Reservation,
    passenger: Passenger,
    ancillary: any,
    type: string
  ) {
    const { flightSummary } = reservation;
    const currencyAncillary = ancillary.currencyPerLeg.find(
      (c) => c.legCode === flightSummary.legCode
    );

    let travelInfo: any = {
      ...this.mapPassengerBasicInfo(passenger),
      ffNumber: passenger.passengerInfo.ffNumber,
      ffTierLevel: this.resolvePassengerFfTier(passenger),
      eticket: passenger.ticketInfo?.eticket,
      ancillaries: [
        {
          ancillary: {
            id: ancillary.order,
            rficSubcode: ancillary.rficSubcode,
            groupCode: ancillary.groupCode,
            emdType: ancillary.emdType,
            rficCode: ancillary.rficCode,
            ssrCode: ancillary.ssrCode,
            owningCarrierCode: ancillary.owningCarrierCode,
            currency: this.buildCurrencyStructure(currencyAncillary?.currency),
          },
          redemptionType: REDEMPTION_TYPES[type] || REDEMPTION_TYPES.tier,
          legCode: currencyAncillary?.legCode,
          quantity: 1,
        },
      ],
    };
    if (type == 'cobrand') {
      travelInfo.cobrandCard = passenger.benefits.cobrandCode; //'AMEXPLT';
    }
    if (type == 'corporate') {
      travelInfo.corporateAccount = reservation?.clid || '';
    }
    const segment = flightSummary.currentSegment;
    return {
      transactionDate: moment().format('YYYY-MM-DDTHH:mm:ss'), //"2025-01-30T13:20:41.266747200"
      travelerInfo: [travelInfo],
      legs: [
        {
          legCode: flightSummary.legCode,
          legRegion: flightSummary.legRegion,
          segments: [this.mapSegmentToApiFormat(segment)],
        },
      ],
    };
  }

  /**
   * Procesa respuesta genérica validando warnings y errores
   * @param response Respuesta de la API
   * @returns Respuesta procesada o lanza error si hay warnings/errores
   */
  static mapResponse(response: any) {
    if (!response) {
      throw new Error('Response is required');
    }

    const { warnings } = response;
    if (warnings?.length > 0) {
      throw new Error(warnings[0]);
    }

    // CI-19277: Validar TODOS los ancillaries de TODOS los pasajeros
    for (const traveler of response.travelerInfo || []) {
      for (const ancillaryItem of traveler.ancillaries || []) {
        const reason = ancillaryItem?.ancillary?.reason;
        if (reason) {
          throw new Error(reason);
        }

        // CI-19277: Detectar maletas que quedaron en ONHOLD como señal de fallo silencioso
        const status = ancillaryItem?.ancillary?.status;
        if (status?.toLowerCase() === 'onhold') {
          throw new Error('ANCILLARY_ONHOLD');
        }
      }
    }

    return response;
  }

  static mapResponsePay(response) {
    const reason = response?.reason;
    if (reason) {
      throw new Error(reason);
    }
    return response;
  }

  static mapResponseReciptPay(response) {
    const reason = response?.reason;
    if (reason) {
      throw new Error(reason);
    }

    const pectabData = response?.pectabReceipt?.pectabData || null;
    const receiptData = response?.pectabReceipt?.receiptData || null;
    const transactionId = response?.receiptMetadata?.transactionId || null;
    const recordLocator = response?.receiptMetadata?.recordLocator || null;
    const totalAmount = response?.receiptMetadata?.totalAmount || null;

    return {
      success: response?.success ?? false,
      pectabData,
      receiptData,
      transactionId,
      recordLocator,
      totalAmount,
    };
  }

  /**
   * Genera request para asignar asientos
   * @param reservation Información de la reserva
   * @returns Objeto request para API de asientos
   */
  static setSeatRequest(
    reservation: Reservation,
    passenger: Passenger,
    seatSelected,
    seatOld,
    rollback
  ) {
    const { flightSummary, isStandBy, passengers, pnr } = reservation;
    const { currency, code } = seatSelected;
    const isCobrand = !!passenger?.benefits?.cobrandCode;
    const isTier = !!passenger?.benefits?.tier && !isCobrand;
    const isChange =
      (seatOld?.seatCode && seatOld?.seatCode?.length > 0 && code != seatOld?.code) || false;

    const validCost = currency && currency?.total > 0;

    const requestSeat = {
      transactionDate: moment(),
      requestFrom: DEFAULT_CONSTANTS.REQUEST_FROM,
      isStandBy: isStandBy,
      reservationCode: pnr,
      passengers: [
        {
          ...this.mapPassengerBasicInfo(passenger),
          ffNumber: passenger?.passengerInfo?.ffNumber || passenger?.frequentFlyerNumber,
          ffTierLevel: passenger?.passengerInfo?.ffTierLevel || '',
          cobrandCard: DEFAULT_CONSTANTS.COBRAND_CARD,
          corporateAccount: reservation?.clid || '',
          seats: [
            {
              /* ...
          seatSelected, */
              rollback: rollback,
              id: seatOld?.id || '',
              seatCode: seatSelected?.code || '',
              isChangeSeat: isChange,
              seatCodeOld: seatOld?.seatCode || '',
              segmentCode: seatSelected?.segmentCode,
              isRedemptionCobrand: isCobrand,
              isRedemptionTier: isTier,
              redemptionType: isCobrand ? 'COBRAND' : isTier ? 'TIER' : '',
              emd: '',
              status: validCost
                ? SeatPassangerEstatus.PENDING_PAYMENT
                : SeatPassangerEstatus.ASSIGNED,
              currency: {
                currencyCode: seatSelected?.currency?.currencyCode || 'MXN',
                base: seatSelected?.currency?.base || 0,
                taxes: seatSelected?.currency?.totalTax || 0,
                total: seatSelected?.currency?.total || 0,
              },
            },
          ],
          ticketInfo: {
            eticket: passenger?.ticketInfo?.eticket,
            coupons: this.mapPassengerCoupons(passenger),
          },
          eticket: passenger?.ticketInfo?.eticket,
        },
      ],
      legs: [
        {
          legCode: flightSummary?.legCode,
          legEntity: flightSummary?.legEntity,
          remainingTimeToCheckin: DEFAULT_CONSTANTS.REMAINING_TIME,
          segments: this.filterSegmentsWithSeats(
            flightSummary?.segments,
            passengers,
            seatSelected?.segmentCode
          ).map((segment, i) => ({
            ...this.mapSegmentToApiFormat(segment),
            remainingSegmentTimeToCheckin: DEFAULT_CONSTANTS.REMAINING_TIME,
            bookingCabin: DEFAULT_CONSTANTS.BOOKING_CABIN,
            segmentNumber: `${i + 1}`,
          })),
        },
      ],
      rollback: false,
    };

    return requestSeat;
  }

  static removeAllSeatsRequest(reservation: Reservation) {
    const { flightSummary, isStandBy, passengers, pnr } = reservation;

    const requestSeat = {
      transactionDate: moment(),
      requestFrom: 'FE_KIOSK',
      isStandBy: isStandBy,
      reservationCode: pnr,
      passengers: passengers
        .filter((passenger) => passenger?.checkinSelected || passenger?.isSelectedToCheckin)
        .map((passenger) => {
          const { seatsNew } = passenger;
          const isCobrand = passenger?.benefits?.cobrandCode ? true : false;
          const isTier = passenger?.benefits?.tier && !isCobrand ? true : false;

          return {
            nameNumber: passenger?.nameRefNumber,
            firstName: passenger?.firstName,
            lastName: passenger?.lastName,
            type: passenger?.paxType,
            ffNumber: passenger?.passengerInfo?.ffNumber || passenger?.frequentFlyerNumber,
            ffTierLevel: passenger?.passengerInfo?.ffTierLevel || '',
            cobrandCard: passenger?.benefits?.cobrandCode || '',
            corporateAccount: reservation?.clid || '',
            seats: seatsNew
              ?.filter(
                (seat) =>
                  seat?.status == SeatPassangerEstatus.PENDING_PAYMENT ||
                  seat?.currency?.total > 0 ||
                  seat?.status != null
              )
              .map((seatNew) => {
                const { seatCode, seatCodeOld, id, segmentCode, currency } = seatNew;
                const validCost = currency && currency?.total > 0;
                const isChange = seatCodeOld?.length > 0 && seatCode != seatCodeOld;

                return {
                  /* ...
                seatSelected, */
                  id: id || '',
                  seatCode: seatCode || '',
                  isChangeSeat: isChange,
                  seatCodeOld: seatCodeOld || '',
                  segmentCode: segmentCode,
                  isRedemptionCobrand: isCobrand,
                  isRedemptionTier: isTier,
                  redemptionType: isCobrand ? 'COBRAND' : isTier ? 'TIER' : '',
                  emd: '',
                  status: validCost
                    ? SeatPassangerEstatus.PENDING_PAYMENT
                    : SeatPassangerEstatus.ASSIGNED,
                  currency: {
                    currencyCode: currency?.currencyCode || 'MXN',
                    base: currency?.base || 0,
                    taxes: currency?.totalTax || 0,
                    total: currency?.total || 0,
                  },
                };
              }),
            ticketInfo: {
              eticket: passenger?.ticketInfo?.eticket,
              coupons: this.mapPassengerCoupons(passenger),
            },
            eticket: passenger?.ticketInfo?.eticket,
          };
        }),
      legs: [
        {
          legCode: flightSummary?.legCode,
          legEntity: flightSummary?.legEntity,
          remainingTimeToCheckin: flightSummary?.remainingTimeToCheckin,
          segments: flightSummary?.segments
            ?.filter((seg) =>
              passengers.find((p) => p.seats.find((s) => s.segmentCode === seg.segmentCode))
            )
            ?.map((segment, i) => ({
              segmentCode: segment?.segmentCode,
              remainingSegmentTimeToCheckin: segment?.remainingSegmentTimeToCheckin,
              aircraftType: segment?.aircraftType,
              origin: segment?.departureAirport,
              destination: segment?.arrivalAirport,
              marketingFlightCode: segment?.marketingFlightCode,
              marketingCarrier: segment?.marketingCarrier,
              operatingCarrier: segment?.operatingCarrier,
              operatingFlightCode: segment?.operatingFlightCode,
              departureDate: segment?.departureDateTime,
              arrivalDate: segment?.arrivalDateTime,
              fareFamily: segment?.fareFamily,
              bookingClass: segment?.bookingClass,
              cabinClass: segment?.cabinClass,
              bookingCabin: segment?.cabinClass,
              departureAirportTimeZoneId: segment?.departureAirportTimeZoneId,
              segmentNumber: `${i + 1}`, // Pendiente
              segmentEntity: segment?.segmentEntity,
            })),
        },
      ],
      rollback: true,
    };

    return requestSeat;
  }

  static respSeat(res): ResponseSeatInt[] {
    const { warnings } = res;

    if (warnings && warnings.length > 0) {
      throw new Error(warnings[0]);
    }

    if (!res || !res.length) {
      throw new Error('La respuesta del servidor es vacía');
    }

    return res.map((item) => ({
      nameNumber: item.nameNumber,
      seats: item.seats.map((seat) => ({
        id: seat.airExtraItemNumber,
        airExtraItemNumber: seat.airExtraItemNumber,
        emdType: seat.emdType,
        code: seat.code,
        endType: seat.endType,
        group: seat.group,
        isChangeSeat: seat.isChangeSeat,
        isChangesSeat: seat.isChangesSeat,
        name: seat.name,
        owningCarrierCode: seat.owningCarrierCode,
        redemptionType: seat.redemptionType,
        rfic: seat.rfic,
        seatCode: seat.seatCode,
        seatCodeOld: seat.seatCodeOld,
        segmentCode: seat.segmentCode,
        segmentIndicator: seat.segmentIndicator,
        ssrCode: seat.ssrCode,
        vendor: seat.vendor,
      })),
    }));
  }

  /**
   * Procesa respuesta de eliminación de servicios adicionales
   * @param response Respuesta de la API
   * @returns Respuesta procesada o lanza error si hay warnings/errores
   */
  static mapRemoveAncillariesResponse(response: any) {
    const { warnings = [], travelerInfo = [] } = response;
    if (warnings.length > 0) {
      throw new Error(warnings[0]);
    }
    // Recorremos cada viajero y sus ancillaries para detectar errores
    for (const traveler of travelerInfo) {
      if (traveler.ancillaries?.length > 0) {
        const hasError = traveler.ancillaries.find(
          (ancillary) => ancillary?.ancillary?.reason && ancillary.ancillary.reason !== ''
        );
        if (hasError) {
          throw new Error(hasError.ancillary.reason);
        }
      }
    }

    return response;
  }

  static buildBenefitBySegmentRequest(reservation: Reservation): BenefitBySegmentRequestInt {
    const { flightSummary, passengers, isEmployee, isStandBy, isNewBF } = reservation;
    const { currentSegment, legCode, legRegion } = flightSummary;
    const ticketAdt =
      passengers.find((p) => p.paxType !== PAX_TYPE.INFANT)?.ticketInfo?.eticket ?? '';
    const ticketInf =
      passengers.find((p) => p.paxType === PAX_TYPE.INFANT)?.ticketInfo?.eticket ?? '';

    return {
      benefitBySegment: [
        {
          legCode,
          segments: [
            {
              segmentCode: currentSegment.segmentCode,
              fareFamily:
                currentSegment.fareFamily === 'BASIC' ? 'MAIN_BASIC' : currentSegment.fareFamily,
              isEmployee,
              isStandBy,
              legRegion: currentSegment.segmentRegion ?? legRegion,
              ticketAdt,
              ticketInf,
              isNewBF,
            },
          ],
        },
      ],
    };
  }

  /**
   * Extrae BaggageAndServicesInt del response anidado de benefitBySegment
   * para reutilizar mapAncillariesFare
   */
  static unwrapBenefitBySegmentResponse(
    response: BenefitBySegmentResponseInt,
    currentSegmentCode: string
  ): BaggageAndServicesInt {
    const leg = response?.benefitBySegment?.[0];
    if (!leg?.success || !leg.segments?.length) {
      throw new Error('benefitBySegment response without valid benefits');
    }

    const segment =
      leg.segments.find((s) => s.segmentCode === currentSegmentCode) ?? leg.segments[0];

    if (!segment?.benefits) {
      throw new Error('benefitBySegment segment without benefits');
    }

    return segment.benefits;
  }

  /**
   * Mapea la respuesta de la api de obtener el costo de los
   * servicios de equipaje y otros servicios adicionales.
   *
   * @param response respuesta de la api
   * @param reservation informacion de la reserva
   * @returns el costo mapeado de los servicios de equipaje y otros servicios adicionales.
   */
  static mapAncillariesFare(response: BaggageAndServicesInt, reservation: Reservation) {
    const { carryOn, checkedBags, seatSelection, infantCheckedBaggage } = response;
    const { passengers } = reservation;

    const baggageIncludes = passengers.map((passenger) => {
      const { nameRefNumber, hasInfant } = passenger;

      const offerCarryOn = this.createCarryOnBags(carryOn);
      const offerCheckBags = this.createCheckedBags(checkedBags, hasInfant, infantCheckedBaggage);

      return { nameRefNumber, offerCheckBags, offerCarryOn };
    });

    return {
      baggageIncludes,
      seatSelection,
      checkedBagsQuantities: checkedBags?.quantities ?? null,
    };
  }

  static requestBagtag(reservation: Reservation, passenger: Passenger, bag: BagsPassangerInt) {
    const { pnr: reservationCode, codeShare, flightSummary } = reservation;
    const codeBagge = (bag?.rficSubcode || bag?.rficCode) ?? '08K';

    return {
      reservationCode,
      bookedLegCollection: [
        {
          codeShare,
          legCode: flightSummary.legCode,
          segments: flightSummary.segments
            .filter((segment) => segment.windowSegmentStatus === 'CKI')
            .map((segment) => ({
              bookingClass: segment.bookingClass,
              departureAirport: segment.departureAirport,
              departureDateTime: segment.departureDateTime,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              segmentCode: segment.segmentCode,
              baggageDropAllowed: true,
              arrivalAirport: segment.arrivalAirport,
            })),
        },
      ],
      passengers: [
        {
          shortBaggageDrop: passenger.shortBaggageDrop,
          lastName: passenger.lastName,
          passengerIds:
            passenger.passengerIds?.map((passengerId) => ({
              id: passengerId.id,
              segmentCode: passengerId.segmentCode,
            })) || [],
          baggageInfo: {
            codeBagge,
            unit: 'KG',
            weight: bag?.weightBag?.toString()?.padStart(2, '0') || '02',
          },
          passengerNameNumber: passenger.nameRefNumber,
          beneficiaryTier: passenger?.frequentFlyerName || '',
          type: this.getPaxTypeShort(passenger.paxType),
        },
      ],
    };
  }

  /**
   * Convierte el tipo de pasajero al formato corto requerido
   * @param paxType Tipo de pasajero largo
   * @returns Tipo de pasajero en formato corto
   */
  private static getPaxTypeShort(paxType: string): string {
    const paxTypeMapping = {
      ADULT: 'ADT',
      CHILD: 'CHD',
      INFANT: 'INF',
    };
    return paxTypeMapping[paxType] || 'ADT';
  }

  //#region CI-21667 / CI-22484 - Payments Compose (/pc/emit-ticket/ancillary)

  //Normaliza cualquier importe del carrito a número
  private static toPaymentAmount(value: unknown): number {
    const amount = Number(value ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  }

  /**
   * Normaliza la cantidad del producto a un entero >= 1: el backend rechaza la emisión
   * con `ERR.PC.EMIT_TICKET_ANCILLARY.BAD_REQUEST` si `FeeDetails` es 0.
   * No basta un `??` porque el 0 llega explícito desde los ancillaries del API.
   */
  private static toPaymentQuantity(value: unknown): number {
    const quantity = Math.trunc(Number(value));
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  /**
   * Construye el legCode del primer segmento (ORIGEN_DESTINO_YYYY-MM-DD).
   * CI-18515: el backend solo acepta el primer tramo.
   */
  private static buildFirstSegmentLegCode(flightSummary: FlightSummary): string {
    const firstSegment = flightSummary?.segments?.[0];

    if (firstSegment) {
      const departureDate = firstSegment.departureDateTime?.split('T')[0] || '';
      const { departureAirport, arrivalAirport } = firstSegment;

      if (departureAirport && arrivalAirport && departureDate) {
        return `${departureAirport}_${arrivalAirport}_${departureDate}`;
      }
    }

    return flightSummary?.legCode || '';
  }

  /**
   * Diccionario que mapea el groupCode del producto al campo `type` del payload de emisión.
   * - BG (maletas, sobrepeso, carry-on) → ITINERARY_PART
   * - TS (seguros / travel services) → ITINERARY
   * - SA (asientos) → SEGMENT
   * Cualquier otro grupo no mapeado cae en ITINERARY_PART como default seguro.
   */
  private static readonly PRODUCT_TYPE_BY_GROUP: Record<string, string> = {
    BG: 'ITINERARY_PART',
    SA: 'SEGMENT',
    TS: 'ITINERARY',
    IE: 'ITINERARY',
    '99': 'ITINERARY_PART',
  };

  private static resolveProductType(groupCode: string): string {
    return this.PRODUCT_TYPE_BY_GROUP[groupCode?.toUpperCase()] || 'ITINERARY_PART';
  }

  /**
   * Normaliza el emdType al valor esperado por el backend.
   * Las maletas llegan con el código numérico ('2') y los asientos con el texto
   * ya resuelto ('Associated'), por eso se aceptan ambos formatos.
   */
  private static mapPaymentEmdType(emdType: unknown): string {
    const value = `${emdType ?? ''}`;
    return value === '2' || value.toLowerCase() === 'associated' ? 'Associated' : 'Standalone';
  }

  /**
   * Detalle de impuestos dentro de currency.
   * Acepta el arreglo de ancillaries ([{ price, description }]) y el objeto
   * legado de asientos ({ MXA: 221 }); si no hay detalle arma un impuesto
   * único con el total de impuestos.
   */
  private static mapPaymentTaxDetails(currency: any): PaymentTaxDetailInt[] {
    const taxDetails = currency?.taxDetails;

    if (Array.isArray(taxDetails) && taxDetails.length > 0) {
      return taxDetails.map((tax) => ({
        price: this.toPaymentAmount(tax?.price),
        description: tax?.description || DEFAULT_CONSTANTS.TAX_CODE,
        exempt: false,
      }));
    }

    if (taxDetails && typeof taxDetails === 'object') {
      const legacyTaxDetails = Object.entries(taxDetails).map(([description, price]) => ({
        price: this.toPaymentAmount(price),
        description,
        exempt: false,
      }));

      if (legacyTaxDetails.length > 0) {
        return legacyTaxDetails;
      }
    }

    return [
      {
        price: this.toPaymentAmount(currency?.totalTax ?? currency?.taxes),
        description: DEFAULT_CONSTANTS.TAX_CODE,
        exempt: false,
      },
    ];
  }

  //Nodo currency del producto
  private static mapPaymentCurrency(currency: any, currencyCode: string): PaymentCurrencyInt {
    const total = this.toPaymentAmount(currency?.total);

    return {
      currencyCode: currency?.currencyCode || currencyCode,
      base: this.toPaymentAmount(currency?.base),
      taxes: this.toPaymentAmount(currency?.totalTax ?? currency?.taxes),
      total,
      totalPaymentForPoints: 0,
      totalPaymentForCard: total,
      taxDetails: this.mapPaymentTaxDetails(currency),
    };
  }

  //Unifica maletas, carry-on, carry-on de oferta y asientos en el producto de emisión
  private static mapPaymentProduct(
    product: any,
    legCode: string,
    currencyCode: string
  ): PaymentProductInt {
    return {
      purchaseType: 'REVENUE',
      name: product?.commercialName || product?.nameDescription || product?.name || '',
      code: product?.rficSubcode || product?.code || '',
      owningCarrierCode: product?.owningCarrierCode || 'AM',
      emdType: this.mapPaymentEmdType(product?.emdType),
      groupCode: product?.groupCode || product?.group || '',
      id: `${product?.airExtraItemNumber ?? product?.id ?? ''}`,
      segmentIndicator: product?.segmentIndicator || '',
      rfic: product?.rficCode || product?.rfic || '',
      ssrCode: product?.ssrCode || 'ASVC',
      vendor: product?.vendor || 'MMGR',
      quantity: this.toPaymentQuantity(product?.quantity),
      legCode: product?.legCode || legCode,
      currency: this.mapPaymentCurrency(product?.currency, currencyCode),
      type: this.resolveProductType(product?.groupCode || product?.group || ''),
    };
  }

  //Productos por cobrar del pasajero
  private static getPaymentProducts(
    passenger: Passenger,
    legCode: string,
    currencyCode: string
  ): PaymentProductInt[] {
    const isPayableBag = (bag: any) =>
      (bag?.status === StatusBag.TO_PAY || bag?.isExtraWeight) && bag?.selected;

    const bags = passenger?.bagStack?.filter(isPayableBag) || [];
    const carryOnBags = passenger?.bagStackCarryon?.filter(isPayableBag) || [];
    const carryOnOfferBags = passenger?.bagOfferCarryOnStack?.filter(isPayableBag) || [];
    const seats =
      passenger?.seatsNew?.filter(
        (seat) => seat?.status === SeatPassangerEstatus.PENDING_PAYMENT
      ) || [];

    return [...bags, ...carryOnBags, ...carryOnOfferBags, ...seats].map((product) =>
      this.mapPaymentProduct(product, legCode, currencyCode)
    );
  }

  //Pasajeros con productos por cobrar
  private static getPaymentPassengers(
    passengers: Passenger[],
    legCode: string,
    currencyCode: string
  ): PaymentPassengerInt[] {
    return passengers
      .map((passenger) => {
        const products = this.getPaymentProducts(passenger, legCode, currencyCode);

        if (products.length === 0) {
          return null;
        }

        return {
          firstName: passenger?.firstName,
          lastName: passenger?.lastName,
          type: this.getPaxTypeShort(passenger?.paxType),
          nameRefNumber: passenger?.nameRefNumber,
          ticketInfo: {
            eticket: passenger?.ticketInfo?.eticket,
            coupons: this.mapPassengerCoupons(passenger),
          },
          products,
        };
      })
      .filter((passenger): passenger is PaymentPassengerInt => passenger !== null);
  }

  /**
   * Itinerario del payload de emisión.
   * Se envían todos los segmentos del leg en check-in; el couponNumber se cruza
   * por segmentCode contra los cupones del boleto.
   */
  private static getPaymentFlightDetails(
    flightSummary: FlightSummary,
    passengers: Passenger[],
    legCode: string
  ): PaymentFlightDetailInt[] {
    const coupons = passengers.flatMap((passenger) => passenger?.ticketInfo?.coupons || []);

    return [
      {
        legCode,
        windowLegStatus: flightSummary?.windowLegStatus,
        segments:
          flightSummary?.segments?.map((segment, index) => {
            const couponNumber = coupons.find(
              (coupon) => coupon?.segmentCode === segment?.segmentCode
            )?.couponNumber;

            return {
              segmentCode: segment?.segmentCode,
              segmentNumber: String(segment?.sequence ?? index + 1),
              marketingCarrier: segment?.marketingCarrier,
              marketingFlightCode: segment?.marketingFlightCode,
              operatingCarrier: segment?.operatingCarrier,
              operatingFlightCode: segment?.operatingFlightCode,
              bookingClass: segment?.bookingClass,
              departureAirport: segment?.departureAirport,
              departureDateTime: segment?.departureDateTime,
              arrivalAirport: segment?.arrivalAirport,
              arrivalDateTime: segment?.arrivalDateTime,
              departureCountry: segment?.departureCountry,
              cabinClass: segment?.cabinClass,
              fareFamily: segment?.fareFamily === 'BASIC' ? 'MAIN_BASIC' : segment?.fareFamily,
              remainingSegmentTimeToCheckin: segment?.remainingSegmentTimeToCheckin,
              departureAirportTimeZoneId: segment?.departureAirportTimeZoneId,
              couponNumber: String(couponNumber ?? index + 1),
            };
          }) || [],
      },
    ];
  }

  //Total a cobrar sobre currency.total del payload
  private static totalPaymentProducts(passengers: PaymentPassengerInt[]): number {
    return passengers.reduce(
      (total, passenger) =>
        total +
        (passenger?.products || []).reduce(
          (subtotal, product) => subtotal + this.toPaymentAmount(product?.currency?.total),
          0
        ),
      0
    );
  }

  /**
   * Genera el request de emisión de ancillaries con pago.
   * POST /pc/emit-ticket/ancillary
   *
   * @param reservation Información de la reserva
   * @param currency Código de moneda de respaldo cuando el producto no lo trae
   * @param pcc Punto de venta (cityCode, stationNumber, accountCode)
   * @param payData Datos de tarjeta y autorización del PinPad
   */
  static emitTicketAncillaryRequest(
    reservation: Reservation,
    currency: string,
    pcc: PaymentPccInt,
    payData: PaymentInfoInt
  ): EmitTicketAncillaryRequestInt {
    const { passengers: passengersReservation, pnr, flightSummary } = reservation;
    const currencyCode = currency || DEFAULT_CONSTANTS.CURRENCY_CODE;
    const legCode = this.buildFirstSegmentLegCode(flightSummary);
    const passengers = this.getPaymentPassengers(passengersReservation, legCode, currencyCode);
    const total = this.totalPaymentProducts(passengers);

    // Se mantiene el comportamiento actual: MMYY de la fecha en curso
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const expireDate = month + year;

    return {
      reservationCode: pnr,
      total,
      isPinPad: true,
      passengers,
      flightDetails: this.getPaymentFlightDetails(flightSummary, passengersReservation, legCode),
      paymentDetails: [
        {
          cardCode: payData?.cartInfo?.cardCode ?? '',
          cardNumber: payData?.cartInfo?.ccNumber ?? '',
          cardSecurityCode: '0000',
          expireDate,
          amount: total,
          transactionID: payData?.autInfo?.transactionId || payData?.autInfo?.operationCode || '',
          approvalCode: payData?.autInfo?.approvalCode ?? '',
          idPaymentDetail: 1,
        },
      ],
      pcc,
    };
  }

  //#endregion CI-21667 / CI-22484

  private static totalsByProduct(array) {
    const total = array.reduce((acc, passenger) => {
      passenger?.products.forEach((product) => {
        acc += product.total.total; // solo sumamos el total de cada producto
      });
      return acc;
    }, 0);

    return total;
  }

  // Pb emit ticket (payload legado; lo consume paymentReciptRequest vía getPassengerData)
  static paymentRequest(reservation: Reservation, currency: string, pcc, payData: PaymentInfoInt) {
    const { passengers: passengersReservation, pnr, flightSummary } = reservation;
    const passengers = this.getPassengerData(passengersReservation, flightSummary);
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = String(now.getFullYear()).slice(-2);
    const expireDate = month + year;
    const amountTest = this.totalsByProduct(passengers);

    return {
      pnr,
      amount: amountTest,
      currencyCode: currency,
      isPaymentPinPad: true,
      passengers,
      pcc,
      payments: [
        {
          cardCode: payData?.cartInfo?.cardCode,
          cardNumber: payData?.cartInfo?.ccNumber,
          expireDate: expireDate,
          amount: amountTest,
          cardSecurityCode: '0000',
          approvalCode: payData?.autInfo?.approvalCode,
        },
      ],
    };
  }

  private static getPassengerData(passengers: Passenger[], flightSummary: FlightSummary) {
    return passengers
      .map((p) => {
        const { firstName, lastName, nameRefNumber, ticketInfo } = p;
        const products = this.getProductsPerPassenger(p, flightSummary, ticketInfo?.eticket);
        if (products.length > 0) {
          return {
            firstName,
            lastName,
            nameRefNumber,
            ticketNumber: ticketInfo?.eticket,
            products,
          };
        }
        return null;
      })
      .filter((p) => p !== null);
  }

  private static getProductsPerPassenger(
    passenger: Passenger,
    flightSummary: FlightSummary,
    ticket: string
  ) {
    const mapBagToProduct = (el) => {
      const associatedFlights =
        flightSummary?.segments
          ?.filter((seg) => seg.operatingCarrier === 'AM')
          ?.map((seg) => {
            const departureDate = seg.departureDateTime
              ? seg.departureDateTime.substring(0, 10)
              : null;
            const couponNumber = passenger?.ticketInfo?.coupons?.find(
              (cup) => cup.segmentCode === seg.segmentCode
            )?.couponNumber;

            return {
              segmentID: seg.id,
              segmentNumber: seg.sequence,
              carrierCode: seg.operatingCarrier,
              operatingCarrierCode: seg.operatingCarrier,
              flightNumber: seg.marketingFlightCode,
              classOfService: seg.bookingClass,
              departureAirport: seg.departureAirport,
              departureDate,
              arrivalAirport: seg.arrivalAirport,
              ticketNumber: ticket,
              couponNumber,
            };
          }) || [];

      return {
        code: el.rficSubcode,
        name: el.commercialName || el.nameDescription,
        group: el.groupCode,
        segmentIndicator: el.segmentIndicator,
        rfic: el.rficCode,
        emdType: el.emdType == '2' ? 'Associated' : 'Standalone',
        airExtraItemNumber: el.id,
        owningCarrierCode: el.owningCarrierCode ?? 'AM',
        vendor: el.vendor,
        quantity: 1,
        total: {
          base: el.currency?.base ?? 0,
          taxes: el.currency?.totalTax ?? 0,
          total: el.currency?.total ?? 0,
        },
        associatedFlights,
      };
    };

    const bags =
      passenger?.bagStack
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const carryOnBags =
      passenger?.bagStackCarryon
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const carryOnOfferBags =
      passenger?.bagOfferCarryOnStack
        ?.filter((b) => (b.status === StatusBag.TO_PAY || b.isExtraWeight) && b.selected)
        .map(mapBagToProduct) || [];

    const seats =
      passenger?.seatsNew
        ?.filter((s) => s.status === SeatPassangerEstatus.PENDING_PAYMENT)
        .map((el) => {
          const associatedFlights =
            flightSummary?.segments
              ?.filter((seg) => seg.operatingCarrier === 'AM')
              ?.map((seg) => {
                const departureDate = seg.departureDateTime
                  ? seg.departureDateTime.substring(0, 10)
                  : null;

                return {
                  segmentID: seg.id,
                  segmentNumber: seg.sequence,
                  carrierCode: seg.operatingCarrier,
                  operatingCarrierCode: seg.operatingCarrier,
                  flightNumber: seg.marketingFlightCode,
                  classOfService: seg.bookingClass,
                  departureAirport: seg.departureAirport,
                  departureDate,
                  arrivalAirport: seg.arrivalAirport,
                  ticketNumber: ticket,
                };
              }) || [];

          return {
            code: el.code,
            name: el.name,
            group: el.group,
            segmentIndicator: el.segmentIndicator,
            rfic: el.rfic,
            emdType: el.emdType,
            airExtraItemNumber: el.airExtraItemNumber,
            owningCarrierCode: el.owningCarrierCode,
            vendor: el.vendor,
            quantity: 1,
            total: {
              base: el.currency.base,
              taxes: el.currency.totalTax ?? 0,
              total: el.currency.total ?? 0,
            },
            associatedFlights,
          };
        }) || [];

    return [...bags, ...carryOnBags, ...carryOnOfferBags, ...seats];
  }

  /**
   * Genera request para verificar elegibilidad de BagTag
   * @param reservation Información de la reserva
   * @param passengers Lista de pasajeros seleccionados
   * @returns Objeto request para API de elegibilidad BagTag
   */
  static requestElegibilityBagtag(reservation: Reservation, passengers: Passenger[]) {
    const { flightSummary, pnr, isStandBy, codeShare } = reservation;
    return {
      reservationCode: pnr,
      bookedLegCollection: [
        {
          codeShare: codeShare || false,
          legCode: flightSummary.legCode,
          segments: flightSummary.segments
            .filter((segment) => segment.windowSegmentStatus === 'CKI')
            .map((segment) => ({
              bookingClass: segment.bookingClass,
              operatingCarrier: segment.operatingCarrier,
              operatingFlightCode: segment.operatingFlightCode,
              farebasis: segment.farebasis,
              segmentCode: segment.segmentCode,
              windowSegmentStatus: segment.windowSegmentStatus,
              fareFamily: segment.fareFamily,
              departureAirport: segment.departureAirport,
              departureCountry: segment.departureCountry,
              departureDateTime: segment.departureDateTime,
              departureAirportTimeZoneId: segment.departureAirportTimeZoneId,
              arrivalAirport: segment.arrivalAirport,
              arrivalCountry: segment.arrivalCountry,
              arrivalDateTime: segment.arrivalDateTime,
              arrivalAirportTimeZoneId: segment.arrivalAirportTimeZoneId,
              segmentStatusCode: segment.segmentStatusCode,
            })),
        },
      ],
      isStandBy: isStandBy,
      passengers: passengers.map((passenger) => ({
        lastName: passenger.lastName,
        passengerNameNumber: passenger.nameRefNumber,
        type: this.getPaxTypeShort(passenger.paxType),
        ancillaries:
          passenger.bagStack
            .filter((b) => b.selected && !b.used && !!b.rficSubcode)
            ?.map((ancillary) => ({
              status: ancillary.status,
              code: ancillary.rficSubcode,
              nameDescription: ancillary.nameDescription || ancillary.commercialName,
              segmentCode: flightSummary.segments
                .filter((seg) => seg.operatingCarrier == 'AM')
                .map((seg) => seg.segmentCode)
                .join('.'),
              isRedemptionCobrand: ancillary.isRedemptionCobrand || false,
              isRedemptionTier: ancillary.isRedemptionTier || false,
              isRedemptionFF: ancillary.isRedemptionFF || false,
              isRedemptionCorporate: ancillary.isRedemptionCorporate || false,
            })) || [],
      })),
    };
  }

  static paymentReciptRequest(reservation: Reservation, payData: PaymentInfoInt, language) {
    const { passengers: passengersReservation, pnr, flightSummary } = reservation;
    const passengers = this.getPassengerData(passengersReservation, flightSummary);
    const emdItems = this.buildIssuedEmds(passengersReservation);

    return {
      purchaseOrder: {
        recordLocator: pnr,
        paymentInfo: {
          cardNumber: payData.cartInfo?.ccNumber,
          cardHolderName: payData.cartInfo?.cardHolderName ?? '',
          cardType: payData.cartInfo?.cardType ?? '',
          expirationDate: payData.cartInfo?.expiryDate ?? '',
          pinPadNotPresent: true,
          digitalSignatureProvided: false,
          aprname: payData.cartInfo?.cardType ?? '',
          alabel: `${payData.autInfo.cbrand}  ${payData.autInfo.cinstr}`,
          arqc: payData.autInfo.carqc,
          aid: payData.autInfo.caid,
          defaultExpirationDateUsed: false,
          cardTokens: [],
        },
        paymentAmount: {
          total: this.totalsByProduct(passengers),
          currencyCode: 'MXN',
        },
      },
      authorizationResult: {
        approvalCode: payData.autInfo?.approvalCode ?? '',
        merchantAccountNumber: payData.autInfo?.merchantAccountNumber ?? '',
      },
      issuedEmds: emdItems,
      language: language,
    };
  }

  static buildIssuedEmds(passengers) {
    const emdItems = [];
    let weightUnit = '';

    // --- ASIENTOS ---
    const allSeats = passengers.flatMap((p) =>
      (p.seatsNew || []).filter((seat) => seat.status === 3)
    );

    if (allSeats.length > 0) {
      const totalSeats = allSeats.reduce(
        (sum, seat) => sum + (seat.currency?.total || seat.cost || 0),
        0
      );
      emdItems.push({
        type: 'EMD_SEAT',
        quantity: allSeats.length,
        total: totalSeats,
        description: allSeats.map((seat) => seat.seatCode).join(', '),
      });
    }
    // --- MALETAS EXTRA ---
    const allBags = passengers.flatMap((p) =>
      (p.bagStack || []).filter((bag) => bag.status === 'toPay')
    );
    if (allBags.length > 0) {
      const totalBags = allBags.reduce((sum, bag) => sum + (bag.currency?.total || 0), 0);
      emdItems.push({
        type: 'EMD_EXTRA_BAGGAGE',
        quantity: allBags.length,
        total: totalBags,
        description: allBags.map((bag) => bag.commercialName).join(', '),
      });
      weightUnit = 'KG'; // si hay maletas, usamos KG
    }

    // --- CARRY ON ---
    const allCarryons = passengers.flatMap((p) =>
      (p.bagStackCarryon || []).filter((bag) => bag.status === 'toPay')
    );
    if (allCarryons.length > 0) {
      // Separar carry-on de 10kg y 5kg
      const carryOn10kg = allCarryons.filter((bag) => bag.typeBagWeight === TypeBagsWeight._10KG);
      const carryOn5kg = allCarryons.filter((bag) => bag.typeBagWeight !== TypeBagsWeight._10KG);

      if (carryOn10kg.length > 0) {
        const totalCarryons10kg = carryOn10kg.reduce(
          (sum, bag) => sum + (bag.currency?.total || 0),
          0
        );
        emdItems.push({
          type: 'EMD_CARRY_ON_10KG',
          quantity: carryOn10kg.length,
          total: totalCarryons10kg,
          description: carryOn10kg.map((bag) => bag.commercialName).join(', '),
        });
      }

      if (carryOn5kg.length > 0) {
        const totalCarryons5kg = carryOn5kg.reduce(
          (sum, bag) => sum + (bag.currency?.total || 0),
          0
        );
        emdItems.push({
          type: 'EMD_CARRY_ON_5KG',
          quantity: carryOn5kg.length,
          total: totalCarryons5kg,
          description: carryOn5kg.map((bag) => bag.commercialName).join(', '),
        });
      }

      weightUnit = 'KG'; // si hay carry-on, también es KG
    }

    return {
      weightUnit,
      emdItems,
    };
  }
}
